import { useEffect, useState } from 'react';
import {
  apiClient,
  type TaskDetail,
  type TaskStatus,
  type TaskPriority,
  type MemberRow,
  ApiError,
} from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { GlowCheck } from '@/components/GlowCheck';
import { useDialogFocus } from '@/lib/useDialogFocus';

interface Props {
  taskId: string;
  myUserId: string;
  members: MemberRow[];
  onClose: () => void;
  onChanged: () => void;
}

const STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
  { value: 'archived', label: 'Archived' },
];

const PRIORITIES: { value: TaskPriority; label: string; dot: string }[] = [
  { value: 'p0', label: 'P0 · Urgent', dot: 'oklch(0.62 0.22 25)' },
  { value: 'p1', label: 'P1 · High', dot: 'oklch(0.78 0.17 65)' },
  { value: 'p2', label: 'P2 · Normal', dot: 'oklch(0.72 0.14 235)' },
  { value: 'p3', label: 'P3 · Low', dot: 'oklch(0.55 0.01 265)' },
];

export function TaskDrawer({ taskId, myUserId, members, onClose, onChanged }: Props) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string | null>(null);
  const [editBody, setEditBody] = useState<string | null>(null);
  const [newSubtask, setNewSubtask] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');
  const dialogRef = useDialogFocus<HTMLDivElement>();

  async function refresh() {
    try {
      const t = await apiClient.getTask(taskId);
      setTask(t);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to load task');
    }
  }

  useEffect(() => {
    refresh();
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  async function patch(p: Parameters<typeof apiClient.updateTask>[1], fieldKey: string) {
    setSavingField(fieldKey);
    try {
      await apiClient.updateTask(taskId, p);
      await refresh();
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Update failed');
    } finally {
      setSavingField(null);
    }
  }

  async function addSubtask(e: React.FormEvent) {
    e.preventDefault();
    if (!newSubtask.trim() || !task) return;
    setSavingField('subtask');
    try {
      await apiClient.createTask(task.project_id, {
        title: newSubtask.trim(),
        parent_task_id: taskId,
      });
      setNewSubtask('');
      await refresh();
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to add subtask');
    } finally {
      setSavingField(null);
    }
  }

  async function toggleSubtask(subtaskId: string, currentStatus: TaskStatus) {
    const next: TaskStatus = currentStatus === 'done' ? 'todo' : 'done';
    try {
      await apiClient.updateTask(subtaskId, { status: next });
      await refresh();
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed');
    }
  }

  async function destroy() {
    if (!task || confirmDelete !== task.title) return;
    try {
      await apiClient.deleteTask(taskId, confirmDelete);
      onChanged();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Delete failed');
    }
  }

  if (!task) {
    return (
      <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex">
        <div className="flex-1 bg-black/60" onClick={onClose} aria-hidden />
        <div className="w-full max-w-2xl bg-background p-6 font-mono text-xs text-muted-foreground">
          {err ?? 'Loading…'}
        </div>
      </div>
    );
  }

  const isCreator = task.created_by === myUserId;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="task-drawer-title" aria-describedby="task-drawer-desc" className="fixed inset-0 z-50 flex justify-end">
      <div className="flex-1 bg-black/60 animate-in fade-in-0 duration-150" onClick={onClose} aria-hidden />
      <div ref={dialogRef} className="flex w-full sm:max-w-2xl flex-col bg-popover shadow-2xl animate-in slide-in-from-right-4 fade-in-0 duration-150">
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Task
            </div>
            <div id="task-drawer-title" className="truncate text-sm font-semibold">{task.title}</div>
            <div id="task-drawer-desc" className="sr-only">Task detail panel — edit title, status, priority, assignee, due date, description, and subtasks.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close task drawer (Esc)"
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            <span aria-hidden>Esc</span>
          </button>
        </header>
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {err && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
              {err}
            </div>
          )}

          {/* Title — heading paired with edit button. Commits on Enter OR blur
              so a click-Save flow doesn't lose the typed title (blur fires before
              click in some browsers; previous behavior set editTitle=null
              silently, discarding the edit). */}
          {editTitle !== null ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                patch({ title: editTitle.trim() }, 'title').then(() => setEditTitle(null));
              }}
            >
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                autoFocus
                aria-label="Task title"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setEditTitle(null);
                  }
                }}
                onBlur={() => {
                  const next = editTitle.trim();
                  if (next && next !== task.title) {
                    patch({ title: next }, 'title').then(() => setEditTitle(null));
                  } else {
                    setEditTitle(null);
                  }
                }}
                className="block w-full rounded-md border border-border bg-card px-3 py-2 text-lg font-semibold focus:border-ring focus:outline-none"
              />
            </form>
          ) : (
            <div className="group flex items-start gap-2">
              <h1 className="flex-1 text-2xl font-semibold tracking-tight">{task.title}</h1>
              <button
                type="button"
                onClick={() => setEditTitle(task.title)}
                aria-label="Edit task title"
                className="mt-1 rounded-md border border-border/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70 transition-all duration-150 hover:border-ring/60 hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
              >
                Edit
              </button>
            </div>
          )}

          {/* Fields */}
          <div className="grid grid-cols-[120px_1fr] gap-y-3">
            <FieldLabel>Status</FieldLabel>
            <select
              value={task.status}
              onChange={(e) => patch({ status: e.target.value as TaskStatus }, 'status')}
              disabled={savingField === 'status'}
              className="rounded-md border border-border bg-card px-2 py-1 text-sm"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>

            <FieldLabel>Priority</FieldLabel>
            <select
              value={task.priority}
              onChange={(e) => patch({ priority: e.target.value as TaskPriority }, 'priority')}
              disabled={savingField === 'priority'}
              className="rounded-md border border-border bg-card px-2 py-1 text-sm"
            >
              {PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>

            <FieldLabel>Assignee</FieldLabel>
            <div className="flex items-center gap-2">
              <select
                value={task.assignee_user_id ?? ''}
                onChange={(e) =>
                  patch({ assignee_user_id: e.target.value || null }, 'assignee')
                }
                disabled={savingField === 'assignee'}
                className="flex-1 rounded-md border border-border bg-card px-2 py-1 text-sm"
              >
                <option value="">— unassigned —</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user_id === myUserId ? `${m.email} (you)` : m.email}
                  </option>
                ))}
              </select>
              {/* One-tap self-assign / unassign. Faster than hunting for
                  yourself in the dropdown — the highest-frequency action
                  on an unassigned task. */}
              <button
                type="button"
                onClick={() =>
                  patch(
                    {
                      assignee_user_id:
                        task.assignee_user_id === myUserId ? null : myUserId,
                    },
                    'assignee',
                  )
                }
                disabled={savingField === 'assignee'}
                className={cn(
                  'shrink-0 rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors',
                  task.assignee_user_id === myUserId
                    ? 'border-primary bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:border-ring/60 hover:text-foreground',
                )}
                title={task.assignee_user_id === myUserId ? 'Unassign me' : 'Assign to me'}
              >
                {task.assignee_user_id === myUserId ? '✓ me' : '+ me'}
              </button>
            </div>

            <FieldLabel>Due</FieldLabel>
            <div className="space-y-1">
              <input
                type="date"
                value={task.due_at ? new Date(task.due_at).toISOString().slice(0, 10) : ''}
                onChange={(e) =>
                  patch(
                    {
                      due_at: e.target.value
                        ? new Date(e.target.value + 'T17:00:00').toISOString()
                        : null,
                    },
                    'due',
                  )
                }
                disabled={savingField === 'due'}
                aria-describedby="due-tz-hint"
                className="rounded-md border border-border bg-card px-2 py-1 text-sm"
              />
              <p id="due-tz-hint" className="font-mono text-[10px] text-muted-foreground/70">
                5pm in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-1">
            <FieldLabel>Description</FieldLabel>
            {editBody !== null ? (
              <div className="space-y-2">
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={6}
                  autoFocus
                  className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      patch({ body_md: editBody }, 'body').then(() => setEditBody(null));
                    }}
                    className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditBody(null)}
                    className="rounded-md border border-border px-3 py-1 text-xs text-muted-foreground"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : task.body_md ? (
              <button
                type="button"
                onClick={() => setEditBody(task.body_md ?? '')}
                aria-label="Edit description"
                className="block w-full text-left cursor-text whitespace-pre-wrap rounded-md border border-border bg-card p-3 text-sm hover:border-ring/60"
              >
                {task.body_md}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setEditBody('')}
                className="block w-full rounded-md border border-dashed border-border bg-card/40 px-3 py-3 text-left text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground"
              >
                Add a description…
              </button>
            )}
          </div>

          {/* Subtasks */}
          <div className="space-y-2">
            <FieldLabel>
              Subtasks {task.subtasks.length > 0 ? `(${task.subtasks.length})` : ''}
            </FieldLabel>
            <div className="space-y-1 rounded-md border border-border bg-card">
              {task.subtasks.map((st) => (
                <div
                  key={st.task_id}
                  className="flex items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
                >
                  <GlowCheck
                    done={st.status === 'done'}
                    onClick={() => toggleSubtask(st.task_id, st.status)}
                    ariaLabel={st.status === 'done' ? 'Mark not done' : 'Mark done'}
                    size="sm"
                  />
                  <span
                    className={cn(
                      'min-w-0 flex-1 text-sm',
                      st.status === 'done' && 'text-muted-foreground line-through',
                    )}
                  >
                    {st.title}
                  </span>
                </div>
              ))}
              <form onSubmit={addSubtask} className="flex gap-2 p-2">
                <input
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  placeholder="Add a subtask…"
                  className="block flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm focus:border-ring focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={!newSubtask.trim() || savingField === 'subtask'}
                  className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground disabled:opacity-60"
                >
                  +
                </button>
              </form>
            </div>
          </div>

          {/* Meta */}
          <div className="rounded-md border border-border bg-background p-3 font-mono text-[10px] text-muted-foreground">
            <div>task_id: {task.task_id}</div>
            <div>created_by: {task.created_by === myUserId ? 'you' : task.created_by}</div>
            {task.parent_event_id && <div>from event: {task.parent_event_id}</div>}
            <div>created: {new Date(task.created_at).toLocaleString()}</div>
          </div>

          {/* Danger zone */}
          <div className="mt-8 space-y-3 rounded-lg border border-destructive/30 bg-destructive/[0.04] p-5">
            <div className="space-y-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-destructive/80">
                Danger zone
              </div>
              <div className="text-sm font-semibold">Delete this task</div>
              <p className="text-xs text-muted-foreground">
                Soft-deletes the task and all its subtasks. 30-day restore window.
              </p>
            </div>
            {!isCreator ? (
              <div className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
                Only the task creator can delete it.
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Type <span className="font-mono text-foreground">"{task.title}"</span>
                  </label>
                  <input
                    value={confirmDelete}
                    onChange={(e) => setConfirmDelete(e.target.value)}
                    placeholder={task.title}
                    className="block w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm focus:border-destructive focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={destroy}
                  disabled={confirmDelete !== task.title}
                  className="rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground disabled:opacity-50"
                >
                  Delete task
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
      {children}
    </label>
  );
}
